import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/common'
import { SignUpWithInvitationPayload, useAcceptInvitation, errors } from '../hooks/useAuth'
import AuthLayout from '../components/AuthLayout'
import SignUpForm from '../components/SignUpForm'
import { SignUpFormData } from '../components/SignUpForm.schema'

export default function AcceptInvitation({
  onSuccess
}: {
  onSuccess: ({ email, password }: { email: string; password: string }) => void
}) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [serverError, setServerError] = useState<string | null>(null)

  const { mutateAsync: acceptInvitation, isPending: isAcceptingInvitation } = useAcceptInvitation()

  const signup = useCallback(
    async (formData: SignUpFormData) => {
      setServerError(null)

      try {
        // Transform form data to API payload (excluding confirmPassword)
        const payload: SignUpWithInvitationPayload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          invitationCode: formData.invitationCode || ''
        }

        await acceptInvitation(payload)
        await onSuccess({ email: payload.email, password: payload.password })
      } catch (error) {
        if (error instanceof Error && error.message === errors.INVITATION_EMAIL_MISMATCH) {
          setServerError(t('signup.errors.emailMismatch'))
        } else {
          setServerError(t('signup.errors.general'))
        }
        console.error(error)
      }
    },
    [acceptInvitation, onSuccess, t]
  )

  return (
    <AuthLayout>
      <>
        <div className="flex flex-col items-center space-y-4 w-full max-w-lg">
          <div className="w-full space-y-4">
            {serverError && <Alert type="error">{serverError}</Alert>}
            <SignUpForm
              isSigningUp={isAcceptingInvitation}
              submit={signup}
              invitationCode={searchParams.get('code') || ''}
            />
          </div>
        </div>
      </>
    </AuthLayout>
  )
}
